import { apiRequest, getApiBaseUrl } from "@/shared/lib/apiClient";
import { getAuthToken, getStoredAccountType } from "../auth";
import { workContextHeaders } from "@/modules/safecheck/workContext";

export type ProformaStatusApi = "pending" | "approved" | "rejected";
export type ProformaUrgencyApi = "normal" | "urgent" | "emergency";

export type ProformaCoveredActApi = {
  code: string;
  label: string;
  cost: number | null;
  is_included: boolean;
  quantity_limit: number | null;
  time_period?: string | null;
  notes?: string | null;
};

export type ProformaApi = {
  id: number;
  request_number?: string;
  patient: string;
  patient_id: string;
  employee_name?: string;
  employee_id_label?: string;
  patient_type?: string;
  dependent_name?: string;
  package_id?: number;
  package_code: string;
  package_ref: string;
  /** Assureur et package du patient. */
  insurer_id?: number | null;
  insurer_name?: string;
  insurance_package_id?: number | null;
  insurance_package?: string;
  insurance_package_code?: string;
  /** Échéance de traitement et délai restant (secondes, négatif si dépassé ; null si traitée). */
  due_at?: string | null;
  remaining_seconds?: number | null;
  /** Mise en attente par le médecin : motif et date. */
  hold_reason?: string | null;
  montant_approuve?: number | null;
  /** 90 % de la limite annuelle atteints : seul le médecin en chef décide. */
  chief_only?: boolean;
  can_decide?: boolean;
  annual_percent?: number | null;
  held_at?: string | null;
  held_by_name?: string | null;
  hospital_id?: number;
  hospital: string;
  company_id?: number;
  company: string;
  employee_id?: number;
  estimated_amount?: number;
  urgency: ProformaUrgencyApi | string;
  urgency_raw?: string;
  status: ProformaStatusApi | string;
  date: string;
  submitted_at?: string;
  hospital_notes?: string;
  validation_notes?: string;
  validated_at?: string;
  /** Médecin (compte Audit) qui a accordé ou refusé la demande. */
  validated_by_name?: string | null;
  proforma_pdf?: string;
  proforma_pdf_signed?: string;
  covered_acts?: ProformaCoveredActApi[];
  covered_acts_included?: number;
  covered_acts_total?: number;
  covered_acts_truncated?: boolean;
  request_acts?: Array<{
    id: number;
    hospital_act_id?: number;
    code: string;
    label: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    notes?: string;
    approved_quantity?: number | null;
    approved_unit_price?: number | null;
    approved_total?: number | null;
    auditor_note?: string;
  }>;
};

export type ProformaMetaPackage = {
  id: number;
  code: string;
  name: string;
  hospital_id?: number;
};

export type ProformaMetaCompany = {
  id: number;
  name: string;
  insurer_id?: number | null;
};

/** Package : prestataires qui l'acceptent et entreprises autorisées (vides = sans restriction). */
export type ProformaMetaInsurancePackage = {
  id: number;
  insurer_id: number;
  code: string;
  name: string;
  hospital_ids: number[];
  company_ids: number[];
};

export type ProformaMetaHospital = {
  id: number;
  name: string;
};

export type ProformaMeta = {
  packages: ProformaMetaPackage[];
  companies: ProformaMetaCompany[];
  hospitals?: ProformaMetaHospital[];
  insurers?: Array<{ id: number; name: string }>;
  insurance_packages?: ProformaMetaInsurancePackage[];
};

export type CreateProformaActPayload = {
  /** Absent pour un acte de la grille pas encore au catalogue, ou saisi à la main (code facultatif, libellé, prix). */
  hospital_act_id?: number;
  code?: string;
  label?: string;
  quantity: number;
  unit_price: number;
  notes?: string;
};

export type CreateProformaPayload = {
  /** Forfait de l'hôpital : facultatif (les assureurs facturent à l'acte). */
  package_id?: number;
  /** Package du patient. */
  insurance_package_id?: number;
  company_id: number;
  employee_id: number;
  estimated_amount?: number;
  urgency?: string;
  hospital_notes?: string;
  patient_type?: "employee" | "dependent";
  dependent_name?: string;
  proforma_pdf?: File;
  hospital_id?: number;
  acts?: CreateProformaActPayload[];
};

export type ProformasQuery = {
  status?: string;
  urgency?: string;
  q?: string;
};

function proformaPdfBasePath(): string {
  const accountType = getStoredAccountType();
  if (accountType === "admin") return "/admin/proformas";
  if (accountType === "enterprise") return "/insurer/proformas";
  return "/auditor/proformas";
}

/** Saisie des demandes : compte Assurance (l'hôpital envoie la demande à l'assureur) ou Admin. */
function proformasWriteBasePath(): "/insurer/proformas" | "/admin/proformas" {
  return getStoredAccountType() === "enterprise" ? "/insurer/proformas" : "/admin/proformas";
}

export function getProformaPdfUrl(id: number, signed = false): string {
  const suffix = signed ? "/pdf/signed" : "/pdf";
  return `${getApiBaseUrl()}${proformaPdfBasePath()}/${id}${suffix}`;
}

async function fetchProformaPdfBlob(id: number, signed = false, download = false): Promise<Blob> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Connexion requise pour accéder au document.");
  }

  const qs = download ? "?download=1" : "";
  const response = await fetch(`${getProformaPdfUrl(id, signed)}${qs}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...workContextHeaders(),
      Accept: "application/pdf,*/*",
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "PDF introuvable.");
  }

  return response.blob();
}

export async function openProformaPdf(id: number, signed = false): Promise<void> {
  const blob = await fetchProformaPdfBlob(id, signed, false);
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (!popup) {
    URL.revokeObjectURL(url);
    throw new Error("Autorisez les pop-ups pour afficher le PDF.");
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadProformaPdf(
  id: number,
  signed = false,
  fileName?: string,
): Promise<void> {
  const blob = await fetchProformaPdfBlob(id, signed, true);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || (signed ? `proforma-${id}-signed.pdf` : `proforma-${id}.pdf`);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function buildQuery(query: ProformasQuery = {}): string {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.urgency) params.set("urgency", query.urgency);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Hospital */

export async function fetchHospitalProformas(
  query: ProformasQuery = {},
): Promise<{ total: number; proformas: ProformaApi[] }> {
  return apiRequest<{ total: number; proformas: ProformaApi[] }>(
    `/claims/proformas${buildQuery(query)}`,
    { auth: true },
  );
}

export async function fetchHospitalProformaMeta(): Promise<ProformaMeta> {
  return apiRequest<ProformaMeta>("/claims/proformas/meta", { auth: true });
}

export async function fetchHospitalProforma(id: number): Promise<ProformaApi> {
  const response = await apiRequest<{ proforma: ProformaApi }>(`/claims/proformas/${id}`, {
    auth: true,
  });
  return response.proforma;
}

export async function createHospitalProforma(
  payload: CreateProformaPayload,
): Promise<ProformaApi> {
  const hasFile = payload.proforma_pdf instanceof File;
  const body: FormData | Omit<CreateProformaPayload, "proforma_pdf"> = hasFile
    ? new FormData()
    : { ...payload };

  if (hasFile && body instanceof FormData) {
    body.append("package_id", String(payload.package_id));
    body.append("company_id", String(payload.company_id));
    body.append("employee_id", String(payload.employee_id));
    if (payload.estimated_amount !== undefined) {
      body.append("estimated_amount", String(payload.estimated_amount));
    }
    if (payload.urgency) body.append("urgency", payload.urgency);
    if (payload.hospital_notes) body.append("hospital_notes", payload.hospital_notes);
    if (payload.patient_type) body.append("patient_type", payload.patient_type);
    if (payload.dependent_name) body.append("dependent_name", payload.dependent_name);
    if (payload.acts?.length) body.append("acts", JSON.stringify(payload.acts));
    if (payload.proforma_pdf) body.append("proforma_pdf", payload.proforma_pdf);
  }

  const response = await apiRequest<{ proforma: ProformaApi; message?: string }>(
    "/claims/proformas",
    { method: "POST", auth: true, body },
  );
  return response.proforma;
}

export async function fetchAdminProformaMeta(hospitalId?: number): Promise<ProformaMeta> {
  const qs = hospitalId && hospitalId > 0 ? `?hospital_id=${hospitalId}` : "";
  return apiRequest<ProformaMeta>(`${proformasWriteBasePath()}/meta${qs}`, { auth: true });
}

export async function createAdminProforma(payload: CreateProformaPayload): Promise<ProformaApi> {
  if (!payload.hospital_id || payload.hospital_id <= 0) {
    throw new Error("Hôpital obligatoire.");
  }

  const hasFile = payload.proforma_pdf instanceof File;
  const body: FormData | CreateProformaPayload = hasFile ? new FormData() : { ...payload };

  if (hasFile && body instanceof FormData) {
    body.append("hospital_id", String(payload.hospital_id));
    if (payload.package_id) body.append("package_id", String(payload.package_id));
    if (payload.insurance_package_id) body.append("insurance_package_id", String(payload.insurance_package_id));
    body.append("company_id", String(payload.company_id));
    body.append("employee_id", String(payload.employee_id));
    if (payload.estimated_amount !== undefined) {
      body.append("estimated_amount", String(payload.estimated_amount));
    }
    if (payload.urgency) body.append("urgency", payload.urgency);
    if (payload.hospital_notes) body.append("hospital_notes", payload.hospital_notes);
    if (payload.patient_type) body.append("patient_type", payload.patient_type);
    if (payload.dependent_name) body.append("dependent_name", payload.dependent_name);
    if (payload.acts?.length) body.append("acts", JSON.stringify(payload.acts));
    if (payload.proforma_pdf) body.append("proforma_pdf", payload.proforma_pdf);
  }

  const response = await apiRequest<{ proforma: ProformaApi; message?: string }>(
    proformasWriteBasePath(),
    { method: "POST", auth: true, body },
  );
  return response.proforma;
}

export async function updateAdminProforma(
  id: number,
  payload: CreateProformaPayload,
): Promise<ProformaApi> {
  if (!payload.hospital_id || payload.hospital_id <= 0) {
    throw new Error("Hôpital obligatoire.");
  }

  const hasFile = payload.proforma_pdf instanceof File;
  const body: FormData | CreateProformaPayload = hasFile ? new FormData() : { ...payload };

  if (hasFile && body instanceof FormData) {
    body.append("hospital_id", String(payload.hospital_id));
    if (payload.package_id) body.append("package_id", String(payload.package_id));
    if (payload.insurance_package_id) body.append("insurance_package_id", String(payload.insurance_package_id));
    body.append("company_id", String(payload.company_id));
    body.append("employee_id", String(payload.employee_id));
    if (payload.estimated_amount !== undefined) {
      body.append("estimated_amount", String(payload.estimated_amount));
    }
    if (payload.urgency) body.append("urgency", payload.urgency);
    if (payload.hospital_notes) body.append("hospital_notes", payload.hospital_notes);
    if (payload.patient_type) body.append("patient_type", payload.patient_type);
    if (payload.dependent_name) body.append("dependent_name", payload.dependent_name);
    if (payload.acts?.length) body.append("acts", JSON.stringify(payload.acts));
    if (payload.proforma_pdf) body.append("proforma_pdf", payload.proforma_pdf);
  }

  const response = await apiRequest<{ proforma: ProformaApi; message?: string }>(
    `${proformasWriteBasePath()}/${id}`,
    { method: "PUT", auth: true, body },
  );
  return response.proforma;
}

/** Auditor / admin (lecture) */

function proformasBasePath(): string {
  const accountType = getStoredAccountType();
  if (accountType === "admin") return "/admin/proformas";
  if (accountType === "enterprise") return "/insurer/proformas";
  return "/auditor/proformas";
}

/* Compte Assurance : agents du portefeuille et actes des prestataires du réseau, pour saisir une demande. */

export async function fetchInsurerRequestEmployees(companyId: number): Promise<{
  total: number;
  employees: Array<{ id: number; name: string; system_id: string; company_id: number }>;
}> {
  return apiRequest(`/insurer/requests/employees?company_id=${companyId}`, { auth: true });
}

export async function lookupInsurerRequestEmployee(params: { matricule?: string; employee_id?: number; company_id?: number }): Promise<{
  employee: { id: number; employee_name: string; matricule: string; company_id: number; company_name: string };
  family_members: Array<{ id: number; name: string; relationship: string }>;
}> {
  const search = new URLSearchParams();
  if (params.matricule) search.set("matricule", params.matricule);
  if (params.employee_id) search.set("employee_id", String(params.employee_id));
  if (params.company_id) search.set("company_id", String(params.company_id));
  return apiRequest(`/insurer/requests/employees/lookup?${search.toString()}`, { auth: true });
}

export async function fetchInsurerRequestFamily(employeeId: number): Promise<Array<{ id: number; name: string; relationship: string }>> {
  const response = await apiRequest<{ family_members: Array<{ id: number; name: string; relationship: string }> }>(
    `/insurer/requests/employees/${employeeId}/family`,
    { auth: true },
  );
  return response.family_members;
}

export async function fetchInsurerRequestHospitalActs(hospitalId: number, query: { q?: string } = {}) {
  const qs = query.q ? `?q=${encodeURIComponent(query.q)}` : "";
  return apiRequest<{ hospital_id: number; total: number; medical_acts: Array<Record<string, unknown>> }>(
    `/insurer/requests/hospital-acts/${hospitalId}${qs}`,
    { auth: true },
  );
}

export async function fetchAuditorProformas(
  query: ProformasQuery = {},
): Promise<{ total: number; proformas: ProformaApi[] }> {
  return apiRequest<{ total: number; proformas: ProformaApi[] }>(
    `${proformasBasePath()}${buildQuery(query)}`,
    { auth: true },
  );
}

export type ProformaRefaction = {
  id: number;
  request_number: string;
  act: string;
  patient: string;
  package: string;
  company: string;
  hospital: string;
  billed: number;
  retained: number;
};

/** Actes des demandes approuvées pour un montant inférieur à celui demandé. */
export async function fetchProformaRefactions(): Promise<ProformaRefaction[]> {
  const response = await apiRequest<{ refactions: ProformaRefaction[] }>(`${proformasBasePath()}/refactions`, { auth: true });
  return response.refactions;
}

export async function fetchAuditorProforma(id: number): Promise<ProformaApi> {
  const response = await apiRequest<{ proforma: ProformaApi }>(`${proformasBasePath()}/${id}`, {
    auth: true,
  });
  return response.proforma;
}

export async function approveAuditorProforma(
  id: number,
  payload: {
    notes?: string;
    montant_approuve?: number;
    acts?: Array<{ id: number; quantity?: number; unit_price?: number; notes?: string }>;
  } = {},
): Promise<ProformaApi> {
  const response = await apiRequest<{ proforma: ProformaApi; message?: string }>(
    `/auditor/proformas/${id}/approve`,
    { method: "POST", auth: true, body: payload },
  );
  return response.proforma;
}

/** Le médecin met la demande en attente : délai restant (minutes) et motif obligatoires. */
export async function holdAuditorProforma(id: number, minutes: number, reason: string): Promise<ProformaApi> {
  const response = await apiRequest<{ proforma: ProformaApi; message?: string }>(`/auditor/proformas/${id}/hold`, {
    method: "POST",
    auth: true,
    body: { minutes, reason },
  });
  return response.proforma;
}

export async function rejectAuditorProforma(
  id: number,
  reason: string,
  notes?: string,
): Promise<ProformaApi> {
  const response = await apiRequest<{ proforma: ProformaApi; message?: string }>(
    `/auditor/proformas/${id}/reject`,
    {
      method: "POST",
      auth: true,
      body: { reason, ...(notes ? { notes } : {}) },
    },
  );
  return response.proforma;
}

/** Actes proposés pour une demande (Admin) : grille en vigueur de l'assureur, puis catalogue du prestataire. */
export async function fetchAdminRequestActs(hospitalId: number, insurerId?: number) {
  const qs = insurerId ? `?insurer_id=${insurerId}` : "";
  return apiRequest<{ hospital_id: number; total: number; medical_acts: Array<Record<string, unknown>> }>(
    `/admin/proformas/acts/${hospitalId}${qs}`,
    { auth: true },
  );
}
